import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { 
  TrendingUp, Calculator, FileText, CheckCircle, ArrowRight, MessageCircle, 
  Loader2, HardHat, Users, Smartphone, MapPin, BarChart3, PieChart, 
  Wallet, Clock, Camera, Mic, CalendarDays, ClipboardList, Play,
  Shield, Zap, Target, DollarSign, Building2, UserCheck, Eye,
  ChevronRight, Star, Menu, X
} from 'lucide-react';
import { toast } from 'sonner';
import { axiosInstance } from '../App';

const LandingPage = ({ setUser }) => {
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ name: '', email: '', password: '' });
  const [forgotData, setForgotData] = useState({ email: '', whatsapp: '' });
  const [forgotResult, setForgotResult] = useState(null);
  const [forgotStep, setForgotStep] = useState('form');
  const [loading, setLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Estado para vídeo da landing page
  const [videoConfig, setVideoConfig] = useState({
    video_enabled: false,
    video_url: '',
    video_title: '',
    video_description: ''
  });

  // Buscar configuração do vídeo
  useEffect(() => {
    const fetchVideoConfig = async () => {
      try {
        const response = await axiosInstance.get('/landing-config');
        setVideoConfig(response.data);
      } catch (error) {
        console.error('Erro ao buscar config do vídeo:', error);
      }
    };
    fetchVideoConfig();
  }, []);

  // ========== AUTH HANDLERS (Phase 5: JWT) ==========
  const _storeAuth = (data) => {
    const u = data.user || {};
    const user = {
      id: u.id || u.user_id,
      user_id: u.user_id || u.id,
      name: u.name,
      email: u.email,
      role: u.role,
      company_id: u.company_id,
      permissoes: u.permissoes || {},
      categoria: u.categoria || null,
    };
    if (data.access_token) localStorage.setItem('segcore_access_token', data.access_token);
    if (data.refresh_token) localStorage.setItem('segcore_refresh_token', data.refresh_token);
    localStorage.setItem('user', JSON.stringify(user));
    setUser(user);
    return user;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axiosInstance.post('/auth/login', loginData);
      if (response.data.access_token) {
        _storeAuth(response.data);
        toast.success('Login realizado com sucesso!');
        setShowLoginModal(false);
      } else {
        toast.error('Resposta inesperada do servidor.');
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erro ao fazer login. Verifique suas credenciais.';
      toast.error(typeof msg === 'string' ? msg : 'Credenciais inválidas');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axiosInstance.post('/auth/register', registerData);
      if (response.data.access_token) {
        _storeAuth(response.data);
        toast.success('Conta criada com sucesso! Bem-vindo ao SegCore!');
        setShowRegisterModal(false);
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erro ao criar conta. Este email pode já estar cadastrado.';
      toast.error(typeof msg === 'string' ? msg : 'Erro ao registrar');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axiosInstance.post('/auth/forgot-password', {
        email: forgotData.email,
        whatsapp: forgotData.whatsapp
      });
      setForgotResult(response.data);
      setForgotStep('success');
      toast.success('Dados verificados! Envie o link para seu WhatsApp.');
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao verificar dados';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const openWhatsAppWithLink = () => {
    if (!forgotResult) return;
    const cleanNumber = forgotResult.whatsapp.replace(/\D/g, '');
    const phoneNumber = cleanNumber.startsWith('55') ? cleanNumber : `55${cleanNumber}`;
    const resetUrl = `${window.location.origin}/reset-password/${forgotResult.token}`;
    const message = encodeURIComponent(
      `🔐 Link para redefinir sua senha do SegCore:\n\n${resetUrl}\n\n⏰ Este link expira em 1 hora.\n👆 Clique no link acima para definir sua nova senha.`
    );
    window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
  };

  const resetForgotPassword = () => {
    setForgotData({ email: '', whatsapp: '' });
    setForgotResult(null);
    setForgotStep('form');
  };

  const handleWhatsApp = () => {
    window.open('https://wa.link/1ku82x', '_blank');
  };

  // ========== PHONE MOCKUP COMPONENT ==========
  const PhoneMockup = ({ children, className = "" }) => (
    <div className={`relative mx-auto ${className}`}>
      <div className="relative border-[8px] md:border-[12px] border-slate-800 rounded-[2rem] md:rounded-[2.5rem] bg-slate-800 shadow-2xl w-[200px] h-[400px] md:w-[280px] md:h-[560px]">
        <div className="absolute top-2 left-1/2 -translate-x-1/2 w-16 md:w-20 h-4 md:h-5 bg-slate-900 rounded-full"></div>
        <div className="w-full h-full rounded-[1.5rem] md:rounded-[2rem] overflow-hidden bg-slate-900">
          {children}
        </div>
      </div>
    </div>
  );

  // ========== FEATURE CARD COMPONENT ==========
  const FeatureCard = ({ icon: Icon, title, description, accent = false, className = "" }) => (
    <div className={`p-6 md:p-8 rounded-2xl border transition-all duration-300 group hover:shadow-xl ${
      accent 
        ? 'bg-gradient-to-br from-orange-500 to-amber-500 border-orange-400 text-white' 
        : 'bg-gradient-to-br from-slate-900/95 to-slate-800/80 border-slate-700/60 backdrop-blur hover:border-orange-500/30'
    } ${className}`}>
      <div className={`w-12 h-12 md:w-14 md:h-14 rounded-xl flex items-center justify-center mb-4 md:mb-6 ${
        accent ? 'bg-white/20' : 'bg-orange-500/20 border border-orange-500/30 group-hover:bg-orange-500/30'
      } transition-colors`}>
        <Icon className={`w-6 h-6 md:w-7 md:h-7 ${accent ? 'text-white' : 'text-orange-400'}`} />
      </div>
      <h3 className={`text-lg md:text-xl font-bold mb-2 md:mb-3 ${accent ? 'text-white' : 'text-white'}`}>
        {title}
      </h3>
      <p className={`text-sm md:text-base leading-relaxed ${accent ? 'text-orange-100' : 'text-slate-300'}`}>
        {description}
      </p>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* ========== NAVBAR ========== */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 md:w-10 md:h-10 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-5 h-5 md:w-6 md:h-6 text-white" />
              </div>
              <span className="text-xl md:text-2xl font-bold text-slate-900">SegCore</span>
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-4">
              <Button 
                variant="ghost" 
                onClick={() => setShowLoginModal(true)}
                className="text-slate-600 hover:text-orange-500"
                data-testid="nav-login-btn"
              >
                Entrar
              </Button>
              <Button 
                onClick={() => setShowRegisterModal(true)}
                className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white rounded-full px-6"
                data-testid="nav-register-btn"
              >
                Teste Grátis 7 Dias
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-slate-100">
              <div className="flex flex-col gap-3">
                <Button 
                  variant="ghost" 
                  onClick={() => { setShowLoginModal(true); setMobileMenuOpen(false); }}
                  className="w-full justify-center"
                >
                  Entrar
                </Button>
                <Button 
                  onClick={() => { setShowRegisterModal(true); setMobileMenuOpen(false); }}
                  className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white rounded-full"
                >
                  Teste Grátis 7 Dias
                </Button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* ========== HERO SECTION ========== */}
      <section className="relative pt-24 md:pt-32 pb-16 md:pb-24 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        
        {/* Orange Glow Effects */}
        <div className="absolute top-20 right-20 w-96 h-96 bg-orange-500/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-20 w-64 h-64 bg-amber-500/20 rounded-full blur-3xl"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            {/* Left Content */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-orange-500/20 border border-orange-500/30 rounded-full px-4 py-2 mb-6">
                <HardHat className="w-4 h-4 text-orange-400" />
                <span className="text-orange-300 text-sm font-medium">Gestão completa para construtoras e obras</span>
              </div>
              
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
                Sua obra tem{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-300">
                  cronograma
                </span>{' '}
                ou tem esperança?
              </h1>
              
              <p className="text-base md:text-lg lg:text-xl text-slate-300 mb-8 max-w-xl mx-auto lg:mx-0">
                O SegCore transforma planejamento em <strong className="text-orange-300">execução controlada</strong> — com visibilidade total sobre prazos, equipes, etapas e financeiro alinhado.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Button 
                  onClick={() => setShowRegisterModal(true)}
                  className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white rounded-full px-6 md:px-8 py-5 md:py-6 text-base md:text-lg font-semibold shadow-lg shadow-orange-500/30 transition-all hover:scale-105"
                  data-testid="hero-cta-btn"
                >
                  Começar Teste Grátis
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  onClick={handleWhatsApp}
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10 rounded-full px-6 md:px-8 py-5 md:py-6 text-base md:text-lg"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Falar no WhatsApp
                </Button>
              </div>

              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4 md:gap-6 mt-8 text-sm text-slate-400">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-orange-400" />
                  <span>7 dias grátis</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-orange-400" />
                  <span>Sem cartão</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-orange-400" />
                  <span>Cancele quando quiser</span>
                </div>
              </div>
            </div>

            {/* Right - Dashboard preview placeholder (SegCore) */}
            <div className="relative mt-8 lg:mt-0">
              <div className="relative bg-slate-800/50 backdrop-blur rounded-2xl border border-slate-700/50 p-6 shadow-2xl overflow-hidden">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span className="text-slate-500 text-xs ml-2">Painel Financeiro</span>
                </div>
                <div className="space-y-3">
                  <div className="bg-slate-700/40 rounded-lg p-3">
                    <p className="text-[10px] text-slate-400 uppercase tracking-wider">Faturamento do mês</p>
                    <p className="text-white font-bold text-2xl mt-1">R$ 248.530,00</p>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-slate-700/40 rounded-lg p-3">
                      <p className="text-[10px] text-slate-400">A Pagar</p>
                      <p className="text-red-400 font-bold text-base">R$ 87.4K</p>
                    </div>
                    <div className="bg-slate-700/40 rounded-lg p-3">
                      <p className="text-[10px] text-slate-400">A Receber</p>
                      <p className="text-green-400 font-bold text-base">R$ 142.1K</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-4 -left-4 bg-white rounded-xl p-3 shadow-xl border border-slate-100 hidden sm:block">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Lucro líquido</p>
                    <p className="text-lg font-bold text-slate-900">+ R$ 54.7K</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== SEÇÃO DO VÍDEO (Configurável pelo Admin) ========== */}
      {videoConfig.video_enabled && videoConfig.video_url && (
        <section className="py-16 md:py-24 bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8 md:mb-12">
              <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/20 rounded-full px-4 py-2 mb-4">
                <Play className="w-4 h-4 text-orange-400" />
                <span className="text-orange-300 text-sm font-medium">Apresentação</span>
              </div>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4">
                {videoConfig.video_title || 'Veja como funciona'}
              </h2>
              {videoConfig.video_description && (
                <p className="text-base md:text-lg text-slate-400 max-w-2xl mx-auto">
                  {videoConfig.video_description}
                </p>
              )}
            </div>
            
            <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-orange-500/10 border border-white/10">
              <div className="aspect-video bg-black">
                {(videoConfig.video_url.includes('youtube.com') || videoConfig.video_url.includes('youtu.be')) ? (
                  <iframe
                    className="w-full h-full"
                    src={`https://www.youtube.com/embed/${
                      videoConfig.video_url.includes('youtu.be') 
                        ? videoConfig.video_url.split('youtu.be/')[1]?.split('?')[0]
                        : videoConfig.video_url.split('v=')[1]?.split('&')[0]
                    }?rel=0`}
                    title={videoConfig.video_title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : videoConfig.video_url.includes('vimeo.com') ? (
                  <iframe
                    className="w-full h-full"
                    src={`https://player.vimeo.com/video/${videoConfig.video_url.split('vimeo.com/')[1]?.split('?')[0]}`}
                    title={videoConfig.video_title}
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                  />
                ) : null}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ========== COMO FUNCIONA SECTION ========== */}
      <section className="py-16 md:py-24 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4">
              Como funciona o SegCore
            </h2>
            <p className="text-base md:text-lg text-slate-400 max-w-2xl mx-auto">
              Do planejamento à execução, tudo integrado em um único sistema
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {/* Card 1 - Cronograma de Execução */}
            <div className="p-6 md:p-8 bg-gradient-to-br from-orange-950/80 to-orange-900/40 border border-orange-800/50 rounded-2xl backdrop-blur">
              <div className="w-12 h-12 bg-orange-500/20 rounded-xl flex items-center justify-center mb-4 border border-orange-500/30">
                <CalendarDays className="w-6 h-6 text-orange-400" />
              </div>
              <h3 className="text-lg md:text-xl font-bold text-white mb-2">Cronograma de Execução</h3>
              <p className="text-slate-300 text-sm md:text-base">
                Tudo é lançado na preparação da obra no painel do sistema, no formato de etapas com prazo de início e término. Planejamento claro e organizado.
              </p>
            </div>
            
            {/* Card 2 - App do Supervisor */}
            <div className="p-6 md:p-8 bg-gradient-to-br from-green-950/80 to-green-900/40 border border-green-800/50 rounded-2xl backdrop-blur">
              <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center mb-4 border border-green-500/30">
                <Smartphone className="w-6 h-6 text-green-400" />
              </div>
              <h3 className="text-lg md:text-xl font-bold text-white mb-2">App do Supervisor</h3>
              <p className="text-slate-300 text-sm md:text-base">
                O supervisor alimenta o sistema em cada etapa pelo APP. Tudo é visto no escritório em tempo real. Ele também envia o cronograma ao cliente e organiza a obra para o dia seguinte.
              </p>
            </div>
            
            {/* Card 3 - Cliente Informado */}
            <div className="p-6 md:p-8 bg-gradient-to-br from-blue-950/80 to-blue-900/40 border border-blue-800/50 rounded-2xl backdrop-blur">
              <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center mb-4 border border-blue-500/30">
                <Users className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="text-lg md:text-xl font-bold text-white mb-2">Cliente Sempre Informado</h3>
              <p className="text-slate-300 text-sm md:text-base">
                O supervisor envia, via link, o cronograma do dia ao cliente. Sem precisar ir à obra, ele vê fotos, áudios explicativos e o que será realizado amanhã.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== SEÇÃO DE IMAGEM DE OBRAS ========== */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1632862378913-b4fe820ce73b?w=1920&q=80')`,
          }}
        />
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/85 to-slate-900/70" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-orange-500/20 border border-orange-500/30 rounded-full px-4 py-2 mb-6">
              <HardHat className="w-4 h-4 text-orange-400" />
              <span className="text-orange-300 text-sm font-medium">Feito para quem constrói</span>
            </div>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-6">
              Sistema desenvolvido por quem{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-300">
                entende de obras
              </span>
            </h2>
            <p className="text-base md:text-lg text-slate-300 mb-8">
              Cada funcionalidade foi pensada para a realidade de <strong className="text-orange-300">construtoras, reformas e prestadores de serviços</strong>. 
              Do orçamento ao acompanhamento da obra, tudo em um só lugar.
            </p>
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2 bg-slate-800/80 backdrop-blur px-4 py-3 rounded-xl border border-slate-700/50">
                <CheckCircle className="w-5 h-5 text-orange-400" />
                <span className="text-white text-sm font-medium">Markup automático</span>
              </div>
              <div className="flex items-center gap-2 bg-slate-800/80 backdrop-blur px-4 py-3 rounded-xl border border-slate-700/50">
                <CheckCircle className="w-5 h-5 text-orange-400" />
                <span className="text-white text-sm font-medium">Cronograma de obras</span>
              </div>
              <div className="flex items-center gap-2 bg-slate-800/80 backdrop-blur px-4 py-3 rounded-xl border border-slate-700/50">
                <CheckCircle className="w-5 h-5 text-orange-400" />
                <span className="text-white text-sm font-medium">Diário de obra digital</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== MAIN FEATURES BENTO GRID ========== */}
      <section className="py-16 md:py-24 bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4">
              Tudo que você precisa para ver seu{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-amber-500">lucro real</span>
            </h2>
            <p className="text-base md:text-lg text-slate-400 max-w-2xl mx-auto">
              Ferramentas pensadas especialmente para quem trabalha com construção, reformas e prestação de serviços
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {/* Orçamentos com Markup - Large Card */}
            <div className="md:col-span-2 lg:col-span-2 p-6 md:p-8 bg-gradient-to-br from-slate-900/95 to-slate-800/80 border border-slate-700/60 rounded-2xl hover:border-orange-500/30 transition-all backdrop-blur">
              <div className="flex flex-col md:flex-row gap-6 md:gap-8">
                <div className="flex-1">
                  <div className="w-12 h-12 md:w-14 md:h-14 bg-orange-500/20 border border-orange-500/30 rounded-xl flex items-center justify-center mb-4 md:mb-6">
                    <Calculator className="w-6 h-6 md:w-7 md:h-7 text-orange-400" />
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold text-white mb-3">Orçamentos com Markup Inteligente</h3>
                  <p className="text-slate-300 mb-4 text-sm md:text-base">
                    O <strong className="text-orange-400">markup</strong> é o índice que garante que seu preço cubra todos os custos e ainda gere lucro. 
                    Em obras e serviços, isso é crucial: materiais, mão de obra, impostos, despesas fixas... 
                    Nosso sistema calcula automaticamente para você nunca vender abaixo do que deveria.
                  </p>
                  <ul className="space-y-2 text-sm text-slate-300">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-orange-400 flex-shrink-0" />
                      Markup calculado sobre custos reais da empresa
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-orange-400 flex-shrink-0" />
                      Orçamentos detalhados ou rápidos para pequenos serviços
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-orange-400 flex-shrink-0" />
                      Envio direto pelo WhatsApp para o cliente
                    </li>
                  </ul>
                </div>
                <div className="hidden md:block w-48 lg:w-56 flex-shrink-0">
                  <div className="bg-slate-900/80 border border-slate-700 rounded-xl p-4 h-full">
                    <div className="text-xs text-slate-400 mb-2">Exemplo de Markup</div>
                    <div className="space-y-3">
                      <div>
                        <div className="text-xs text-slate-500">Custo Total</div>
                        <div className="font-bold text-white">R$ 8.500</div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500">Markup (1.65)</div>
                        <div className="text-orange-400 font-bold">+ 65%</div>
                      </div>
                      <div className="pt-2 border-t border-slate-700">
                        <div className="text-xs text-slate-500">Preço de Venda</div>
                        <div className="text-xl font-bold text-orange-400">R$ 14.025</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* GPS Financeiro */}
            <FeatureCard 
              icon={MapPin}
              title="GPS Financeiro"
              description="Mostra exatamente quanto você precisa faturar para cobrir suas despesas e começar a lucrar. É o ponto de equilíbrio da sua empresa."
              accent
            />

            {/* DRE */}
            <FeatureCard 
              icon={FileText}
              title="DRE - Demonstração do Resultado"
              description="Veja de forma clara quanto entrou, quanto saiu e quanto sobrou. O DRE mostra a saúde financeira real do seu negócio mês a mês."
            />

            {/* DFC */}
            <FeatureCard 
              icon={BarChart3}
              title="DFC - Fluxo de Caixa"
              description="Saiba exatamente quando o dinheiro entra e sai. Planeje pagamentos, evite surpresas e tome decisões com base em dados reais."
            />

            {/* Cronograma de Obras */}
            <FeatureCard 
              icon={HardHat}
              title="Cronograma de Obras"
              description="Gerencie cada etapa da obra, controle o progresso, defina prazos e mantenha tudo organizado. Da fundação à entrega das chaves."
            />
          </div>
        </div>
      </section>

      {/* ========== APPS ECOSYSTEM ========== */}
      <section className="py-16 md:py-24 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4">
              Apps para toda a equipe
            </h2>
            <p className="text-base md:text-lg text-slate-400 max-w-2xl mx-auto">
              Do canteiro de obras ao escritório, todos conectados em tempo real
            </p>
          </div>

          {/* App do Supervisor */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center mb-16 md:mb-24">
            <div className="order-2 lg:order-1 flex justify-center">
              <PhoneMockup>
                <div className="h-full bg-gradient-to-b from-orange-500 to-amber-600 p-4 text-white">
                  <div className="text-center mb-4 pt-4">
                    <HardHat className="w-10 h-10 mx-auto mb-2" />
                    <h4 className="font-bold text-lg">App do Supervisor</h4>
                  </div>
                  <div className="space-y-2">
                    <div className="bg-white/20 rounded-lg p-3 flex items-center gap-3">
                      <Camera className="w-5 h-5" />
                      <span className="text-sm">Fotos do progresso</span>
                    </div>
                    <div className="bg-white/20 rounded-lg p-3 flex items-center gap-3">
                      <Mic className="w-5 h-5" />
                      <span className="text-sm">Áudios do dia</span>
                    </div>
                    <div className="bg-white/20 rounded-lg p-3 flex items-center gap-3">
                      <ClipboardList className="w-5 h-5" />
                      <span className="text-sm">Diário de obra</span>
                    </div>
                    <div className="bg-white/20 rounded-lg p-3 flex items-center gap-3">
                      <MessageCircle className="w-5 h-5" />
                      <span className="text-sm">Enviar ao cliente</span>
                    </div>
                  </div>
                  <div className="mt-4 p-3 bg-white/10 rounded-lg border border-white/20">
                    <p className="text-xs opacity-80">Programação para amanhã</p>
                    <p className="text-sm font-medium">Equipe: 4 pessoas</p>
                    <p className="text-sm font-medium">Materiais: Verificados ✓</p>
                  </div>
                </div>
              </PhoneMockup>
            </div>
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 bg-orange-500/20 rounded-full px-4 py-2 mb-4">
                <HardHat className="w-4 h-4 text-orange-400" />
                <span className="text-orange-300 text-sm font-medium">App do Supervisor</span>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                Diário de obra direto no WhatsApp do cliente
              </h3>
              <p className="text-slate-300 mb-6 text-sm md:text-base">
                No final do dia, o supervisor registra fotos, áudios e anotações. Com um clique, 
                tudo vai direto para o WhatsApp do cliente. Ele acompanha a obra sem precisar ir pessoalmente.
              </p>
              <ul className="space-y-3 text-slate-300 text-sm md:text-base">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                  <span><strong className="text-white">Diário de obra completo:</strong> Fotos, áudios e textos do progresso diário</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                  <span><strong className="text-white">Pré-orçamentos:</strong> Crie orçamentos rápidos direto da obra, já vai para o sistema</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                  <span><strong className="text-white">Programação do dia seguinte:</strong> Equipe, materiais e equipamentos organizados</span>
                </li>
              </ul>
            </div>
          </div>

          {/* App do Vendedor */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center mb-16 md:mb-24">
            <div>
              <div className="inline-flex items-center gap-2 bg-blue-500/20 rounded-full px-4 py-2 mb-4">
                <Users className="w-4 h-4 text-blue-400" />
                <span className="text-blue-300 text-sm font-medium">App do Vendedor + CRM com IA</span>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                CRM completo com Inteligência Artificial
              </h3>
              <p className="text-slate-300 mb-6 text-sm md:text-base">
                Seus vendedores podem cadastrar clientes, agendar visitas e acompanhar o funil de vendas 
                diretamente do celular. <strong className="text-blue-300">A IA cria automaticamente mensagens personalizadas</strong> para 
                enviar aos clientes em cada etapa da negociação.
              </p>
              <ul className="space-y-3 text-slate-300 text-sm md:text-base">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                  <span><strong className="text-white">Mensagens com IA:</strong> Textos prontos e personalizados para cada cliente e etapa</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                  <span><strong className="text-white">Cadastro rápido:</strong> Cadastre clientes na visita, já vai para o CRM</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                  <span><strong className="text-white">Pipeline visual:</strong> Veja em que etapa cada negócio está</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                  <span><strong className="text-white">Análise inteligente:</strong> A IA sugere a melhor ação para fechar cada venda</span>
                </li>
              </ul>
            </div>
            <div className="flex justify-center">
              <PhoneMockup>
                <div className="h-full bg-gradient-to-b from-blue-500 to-blue-600 p-4 text-white">
                  <div className="text-center mb-4 pt-4">
                    <Users className="w-10 h-10 mx-auto mb-2" />
                    <h4 className="font-bold text-lg">App do Vendedor</h4>
                  </div>
                  <div className="space-y-2">
                    <div className="bg-white/20 rounded-lg p-3">
                      <p className="text-xs opacity-80">Pipeline de Vendas</p>
                      <div className="flex gap-1 mt-2">
                        <div className="flex-1 h-2 bg-white/40 rounded"></div>
                        <div className="flex-1 h-2 bg-white/60 rounded"></div>
                        <div className="flex-1 h-2 bg-white/30 rounded"></div>
                        <div className="flex-1 h-2 bg-white/20 rounded"></div>
                      </div>
                    </div>
                    <div className="bg-white/20 rounded-lg p-3 flex items-center gap-3">
                      <CalendarDays className="w-5 h-5" />
                      <div>
                        <p className="text-sm font-medium">3 visitas hoje</p>
                        <p className="text-xs opacity-80">Próxima: 14:00</p>
                      </div>
                    </div>
                    <div className="bg-white/20 rounded-lg p-3 flex items-center gap-3">
                      <UserCheck className="w-5 h-5" />
                      <span className="text-sm">+ Novo Cliente</span>
                    </div>
                  </div>
                  <div className="mt-4 p-3 bg-orange-500/30 rounded-lg border border-orange-400/30">
                    <p className="text-xs">Meta do mês</p>
                    <p className="text-lg font-bold">R$ 45.000 / R$ 60.000</p>
                    <div className="w-full h-2 bg-white/20 rounded mt-1">
                      <div className="w-3/4 h-full bg-orange-400 rounded"></div>
                    </div>
                  </div>
                </div>
              </PhoneMockup>
            </div>
          </div>

          {/* App do Dono */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            <div className="order-2 lg:order-1 flex justify-center">
              <PhoneMockup>
                <div className="h-full bg-gradient-to-b from-slate-700 to-slate-800 p-4 text-white">
                  <div className="text-center mb-4 pt-4">
                    <Building2 className="w-10 h-10 mx-auto mb-2" />
                    <h4 className="font-bold text-lg">App do Proprietário</h4>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <div className="bg-orange-500/20 rounded-lg p-3 text-center">
                      <p className="text-xs opacity-80">Faturamento</p>
                      <p className="text-lg font-bold text-orange-400">R$ 125k</p>
                    </div>
                    <div className="bg-green-500/20 rounded-lg p-3 text-center">
                      <p className="text-xs opacity-80">Lucro</p>
                      <p className="text-lg font-bold text-green-400">R$ 38k</p>
                    </div>
                  </div>
                  <div className="bg-white/10 rounded-lg p-3 mb-2">
                    <p className="text-xs opacity-80 mb-2">Saúde Financeira</p>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-3 bg-white/20 rounded-full">
                        <div className="w-[85%] h-full bg-gradient-to-r from-orange-500 to-amber-400 rounded-full"></div>
                      </div>
                      <span className="font-bold">85%</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="bg-white/10 rounded-lg p-3 flex justify-between items-center">
                      <span className="text-sm">A Receber</span>
                      <span className="font-bold text-green-400">R$ 42k</span>
                    </div>
                    <div className="bg-white/10 rounded-lg p-3 flex justify-between items-center">
                      <span className="text-sm">A Pagar</span>
                      <span className="font-bold text-red-400">R$ 18k</span>
                    </div>
                  </div>
                </div>
              </PhoneMockup>
            </div>
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 bg-slate-700/50 rounded-full px-4 py-2 mb-4">
                <Building2 className="w-4 h-4 text-slate-300" />
                <span className="text-slate-300 text-sm font-medium">App do Proprietário</span>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                Sua empresa na palma da mão
              </h3>
              <p className="text-slate-300 mb-6 text-sm md:text-base">
                Veja as finanças da sua empresa em tempo real, de qualquer lugar. 
                Faturamento, lucro, contas a pagar e receber, tudo resumido em uma tela.
              </p>
              <ul className="space-y-3 text-slate-300 text-sm md:text-base">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                  <span><strong className="text-white">Visão em tempo real:</strong> Faturamento, lucro e fluxo de caixa atualizados</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                  <span><strong className="text-white">Alertas importantes:</strong> Contas vencendo, metas atingidas</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                  <span><strong className="text-white">Decisões rápidas:</strong> Informação certa na hora certa</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ========== ADDITIONAL FEATURES ========== */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              E muito mais para sua empresa crescer
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            <div className="p-6 bg-slate-50 rounded-2xl border border-slate-100 text-center">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Wallet className="w-6 h-6 text-orange-500" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Controle de Caixa</h3>
              <p className="text-sm text-slate-600">Lançamentos de entradas e saídas organizados por categoria</p>
            </div>
            <div className="p-6 bg-slate-50 rounded-2xl border border-slate-100 text-center">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <PieChart className="w-6 h-6 text-orange-500" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Gráficos Inteligentes</h3>
              <p className="text-sm text-slate-600">Visualize tendências e tome decisões baseadas em dados</p>
            </div>
            <div className="p-6 bg-slate-50 rounded-2xl border border-slate-100 text-center">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Play className="w-6 h-6 text-orange-500" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Passo a Passo em Vídeo</h3>
              <p className="text-sm text-slate-600">Vídeos que ensinam cada função + conteúdos sobre gestão</p>
            </div>
            <div className="p-6 bg-slate-50 rounded-2xl border border-slate-100 text-center">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Shield className="w-6 h-6 text-orange-500" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Dados Seguros</h3>
              <p className="text-sm text-slate-600">Suas informações protegidas com criptografia de ponta</p>
            </div>
          </div>

          {/* Video Content Highlight */}
          <div className="mt-12 md:mt-16 relative rounded-2xl overflow-hidden border border-orange-200">
            {/* Background Image */}
            <div 
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: `url('https://images.unsplash.com/photo-1712904124132-857e6577aab9?w=1200&q=80')`,
              }}
            />
            {/* Overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-orange-50/95 via-amber-50/90 to-orange-50/80" />
            
            <div className="relative p-6 md:p-8">
              <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8">
                <div className="w-16 h-16 md:w-20 md:h-20 bg-orange-500 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-orange-500/30">
                  <Play className="w-8 h-8 md:w-10 md:h-10 text-white" />
                </div>
                <div className="text-center md:text-left flex-1">
                  <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-2">
                    Aprenda enquanto usa
                  </h3>
                  <p className="text-slate-700 text-sm md:text-base">
                    O sistema conta com <strong>videoaulas passo a passo</strong> que guiam você em cada funcionalidade. 
                    Além disso, temos conteúdos exclusivos sobre <strong>tributos, fluxo de caixa, DRE</strong> e 
                    tudo que um empresário precisa entender para fazer sua empresa crescer.
                  </p>
                </div>
                <div className="hidden lg:flex flex-col gap-2 text-sm">
                  <div className="flex items-center gap-2 text-slate-700">
                    <CheckCircle className="w-4 h-4 text-orange-500" />
                    <span>Tutoriais em vídeo</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-700">
                    <CheckCircle className="w-4 h-4 text-orange-500" />
                    <span>Conteúdo de gestão</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-700">
                    <CheckCircle className="w-4 h-4 text-orange-500" />
                    <span>Aulas de finanças</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== FINAL CTA ========== */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-orange-500 to-amber-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-2 mb-6">
            <Star className="w-4 h-4 text-yellow-200" />
            <span className="text-white text-sm font-medium">Comece hoje mesmo</span>
          </div>
          
          <h2 className="text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-6">
            Descubra o lucro real do seu negócio
          </h2>
          
          <p className="text-lg md:text-xl text-orange-100 mb-8 max-w-2xl mx-auto">
            Teste o SegCore por <strong>7 dias grátis</strong>. Sem cartão de crédito, 
            sem compromisso. Se não gostar, é só não continuar.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => setShowRegisterModal(true)}
              className="bg-white text-orange-600 hover:bg-orange-50 rounded-full px-8 py-6 text-lg font-semibold shadow-lg transition-all hover:scale-105"
              data-testid="final-cta-btn"
            >
              Começar Teste Grátis Agora
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              onClick={handleWhatsApp}
              variant="outline"
              className="border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              Tirar Dúvidas
            </Button>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-6 mt-8 text-sm text-orange-100">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              <span>7 dias grátis</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              <span>Sem cartão de crédito</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              <span>Suporte incluído</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              <span>Cancele quando quiser</span>
            </div>
          </div>
        </div>
      </section>

      {/* ========== FOOTER ========== */}
      <footer className="py-8 md:py-12 bg-slate-900 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-amber-500 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-bold text-white">SegCore</span>
            </div>
            <p className="text-slate-400 text-sm text-center">
              © 2025 SegCore - Gestão financeira inteligente para empresas que querem crescer com lucro real.
            </p>
          </div>
        </div>
      </footer>

      {/* ========== MODALS (NÃO MODIFICAR LÓGICA) ========== */}
      
      {/* Modal de Login */}
      <Dialog open={showLoginModal} onOpenChange={setShowLoginModal}>
        <DialogContent className="sm:max-w-md bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-white">Entrar no SegCore</DialogTitle>
            <DialogDescription className="text-slate-400">
              Entre com suas credenciais para acessar o sistema
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="login-email" className="text-white">Email</Label>
              <Input
                id="login-email"
                type="email"
                placeholder="seu@email.com"
                value={loginData.email}
                onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                required
                className="bg-slate-800 border-slate-700 text-white"
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label htmlFor="login-password" className="text-white">Senha</Label>
                <button
                  type="button"
                  onClick={() => {
                    setShowLoginModal(false);
                    setShowForgotPasswordModal(true);
                    setForgotData({ ...forgotData, email: loginData.email });
                  }}
                  className="text-xs text-orange-400 hover:text-orange-300"
                  data-testid="forgot-password-link"
                >
                  Esqueci minha senha
                </button>
              </div>
              <Input
                id="login-password"
                type="password"
                placeholder="••••••••"
                value={loginData.password}
                onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                required
                className="bg-slate-800 border-slate-700 text-white"
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? 'Entrando...' : 'Entrar'}
            </Button>
            <div className="text-center text-sm text-slate-400">
              Não tem conta?{' '}
              <button
                type="button"
                onClick={() => {
                  setShowLoginModal(false);
                  setShowRegisterModal(true);
                }}
                className="text-orange-400 hover:text-orange-300"
              >
                Cadastre-se grátis
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal de Esqueci Minha Senha */}
      <Dialog open={showForgotPasswordModal} onOpenChange={(open) => {
        setShowForgotPasswordModal(open);
        if (!open) resetForgotPassword();
      }}>
        <DialogContent className="sm:max-w-md bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-white">Recuperar Senha</DialogTitle>
            <DialogDescription className="text-slate-400">
              {forgotStep === 'form' 
                ? 'Digite seu email e o WhatsApp cadastrado na empresa'
                : 'Link de recuperação gerado com sucesso!'}
            </DialogDescription>
          </DialogHeader>

          {forgotStep === 'form' && (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="forgot-email" className="text-white">Seu Email</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  placeholder="seu@email.com"
                  value={forgotData.email}
                  onChange={(e) => setForgotData({ ...forgotData, email: e.target.value })}
                  required
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid="forgot-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="forgot-whatsapp" className="text-white">WhatsApp da Empresa</Label>
                <Input
                  id="forgot-whatsapp"
                  type="tel"
                  placeholder="(11) 99999-9999"
                  value={forgotData.whatsapp}
                  onChange={(e) => setForgotData({ ...forgotData, whatsapp: e.target.value })}
                  required
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid="forgot-whatsapp-input"
                />
                <p className="text-xs text-slate-500">
                  Digite o WhatsApp cadastrado nos dados da empresa para verificação
                </p>
              </div>
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600"
                disabled={loading}
                data-testid="forgot-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Verificando...
                  </>
                ) : 'Verificar e Gerar Link'}
              </Button>
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPasswordModal(false);
                    setShowLoginModal(true);
                  }}
                  className="text-sm text-slate-400 hover:text-white"
                >
                  Voltar ao login
                </button>
              </div>
            </form>
          )}

          {forgotStep === 'success' && forgotResult && (
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-orange-500/10 border border-orange-500/30">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-orange-400" />
                  <span className="text-orange-400 font-semibold">Dados verificados!</span>
                </div>
                <p className="text-sm text-slate-300">
                  Olá <span className="text-white font-medium">{forgotResult.user_name}</span>, seu link de recuperação foi gerado.
                </p>
              </div>

              <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/30">
                <p className="text-sm text-slate-300 mb-3">
                  Clique no botão abaixo para abrir o WhatsApp com o link de recuperação. 
                  <span className="text-blue-300 block mt-1">
                    Envie a mensagem para você mesmo ou para um contato de confiança.
                  </span>
                </p>
                <p className="text-xs text-slate-500">
                  ⏰ O link expira em 1 hora
                </p>
              </div>

              <Button
                onClick={openWhatsAppWithLink}
                className="w-full bg-green-600 hover:bg-green-700"
                data-testid="send-whatsapp-btn"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Enviar Link para Meu WhatsApp
              </Button>

              <div className="text-center space-y-2">
                <button
                  type="button"
                  onClick={() => {
                    setForgotStep('form');
                    setForgotResult(null);
                  }}
                  className="text-sm text-slate-400 hover:text-white"
                >
                  Tentar com outro email
                </button>
                <br />
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPasswordModal(false);
                    setShowLoginModal(true);
                    resetForgotPassword();
                  }}
                  className="text-sm text-orange-400 hover:text-orange-300"
                >
                  Voltar ao login
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Registro */}
      <Dialog open={showRegisterModal} onOpenChange={setShowRegisterModal}>
        <DialogContent className="sm:max-w-md bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-white">Comece seu teste grátis</DialogTitle>
            <DialogDescription className="text-slate-400">
              7 dias grátis • Sem cartão de crédito • Cancele quando quiser
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleRegister} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="register-name" className="text-white">Nome Completo</Label>
              <Input
                id="register-name"
                type="text"
                placeholder="Seu nome"
                value={registerData.name}
                onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                required
                className="bg-slate-800 border-slate-700 text-white"
                data-testid="register-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="register-email" className="text-white">Email</Label>
              <Input
                id="register-email"
                type="email"
                placeholder="seu@email.com"
                value={registerData.email}
                onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                required
                className="bg-slate-800 border-slate-700 text-white"
                data-testid="register-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="register-password" className="text-white">Senha</Label>
              <Input
                id="register-password"
                type="password"
                placeholder="Mínimo 6 caracteres"
                value={registerData.password}
                onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                required
                minLength={6}
                className="bg-slate-800 border-slate-700 text-white"
                data-testid="register-password-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? 'Criando conta...' : 'Começar teste grátis agora'}
            </Button>
            <div className="text-center text-sm text-slate-400">
              Já tem conta?{' '}
              <button
                type="button"
                onClick={() => {
                  setShowRegisterModal(false);
                  setShowLoginModal(true);
                }}
                className="text-orange-400 hover:text-orange-300"
              >
                Faça login
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LandingPage;
